import { useEffect } from 'react'
import { canSubmitSelection } from '~/boggle/engine/standard/game'
import { getBoggleCellEffectiveGold } from '~/boggle/engine/standard/effectiveSpecialTiles'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import type { BoggleStore } from '~/boggle/ui/state/Store'

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    return true
  }

  return target.isContentEditable
}

function isInsideOpenDialog(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false
  }

  return Boolean(target.closest('[role="dialog"]'))
}

function cellMatchesLetterKey(cellLetter: string, keyCharUpper: string): boolean {
  const letter = cellLetter.toUpperCase()
  return letter === keyCharUpper || letter.startsWith(keyCharUpper)
}

function findFirstLetterPosition(
  state: BoggleGameState,
  keyCharUpper: string,
): { col: number; row: number } | null {
  const { board, cols, rows } = state
  let firstLetterMatch: { col: number; row: number } | null = null
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const cell = board[row]?.[col]
      if (!cell) {
        continue
      }
      if (!cellMatchesLetterKey(cell.letter, keyCharUpper)) {
        continue
      }
      if (getBoggleCellEffectiveGold(state, cell)) {
        return { col, row }
      }
      if (firstLetterMatch === null) {
        firstLetterMatch = { col, row }
      }
    }
  }
  return firstLetterMatch
}

/**
 * Global keyboard controls while a run is active (`phase === 'playing'`).
 * — Letter keys mirror tapping a matching tile: prefer effective-gold tiles (row-major),
 *   otherwise the first letter match (row-major).
 * — Skill mode uses the same target resolution as pointer input.
 */
export function useBoggleRunKeyboard(store: BoggleStore, enabled: boolean) {
  useEffect(() => {
    if (!enabled) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return
      }

      if (isTextEditingTarget(event.target)) {
        return
      }

      const state = store.getState()
      if (state.phase !== 'playing') {
        return
      }

      if (event.key === 'Escape') {
        if (isInsideOpenDialog(event.target)) {
          return
        }

        if (event.repeat) {
          return
        }

        event.preventDefault()
        store.togglePaused()
        return
      }

      if (store.getPaused()) {
        return
      }

      if (event.key === 'Backspace') {
        if (event.ctrlKey) {
          event.preventDefault()
          store.clearSelection()
          return
        }

        event.preventDefault()
        store.removeLastSelectionEntry()
        return
      }

      if (event.key === 'Enter') {
        if (event.repeat) {
          return
        }

        const live = store.getState()
        if (!canSubmitSelection(live)) {
          return
        }

        event.preventDefault()
        store.submitSelection()
        return
      }

      if (event.key.length !== 1 || !/[a-zA-Z]/.test(event.key)) {
        return
      }

      if (event.ctrlKey || event.metaKey || event.altKey) {
        return
      }

      if (event.repeat) {
        return
      }

      const keyUpper = event.key.toUpperCase()
      const position = findFirstLetterPosition(store.getState(), keyUpper)
      if (!position) {
        return
      }

      event.preventDefault()

      const live = store.getState()
      if (live.activeSkill !== null) {
        store.useActiveSkillOnTile(position)
        return
      }

      store.tapSelection(position)
    }

    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [enabled, store])
}
